import { LoadingButton } from '@mui/lab';
import { IconButton, InputAdornment, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { useNotify } from 'src/hooks/use-notify';
import { fStandardNumber } from 'src/utils/formatNumber';
import * as Yup from 'yup';
import { FormikPasswordView, FormikTextView } from '../../../basic-components/formik-inputs';
import T from '../../../basic-components/text';
import useAuth from '../../../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'src/routes/paths';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { loginByPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotify();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const usingPasswordValidationSchema = Yup.object({
    username: Yup.string()
      .matches(/^(\+98|0098|98|0)?9\d{9}$/, t('invalid-mobile-number'))
      .required(t('required')),
    password: Yup.string().required(t('required')),
  });

  const onSubmitPassword = async (values) => {
    setIsLoading(true);
    loginByPassword(fStandardNumber(values.username), fStandardNumber(values.password))
      .then((res) => {
        notify.success(t(res));

        const from = location.state?.from || PATH_DASHBOARD.root;
        navigate(from, { replace: true });
      })
      .catch((err) => notify.error(t(err)))
      .finally(() => setIsLoading(false));
  };

  return (
    <Formik
      initialValues={{
        username: '',
        password: '',
      }}
      validationSchema={usingPasswordValidationSchema}
      onSubmit={onSubmitPassword}
    >
      {(formik) => (
        <Form>
          <Stack spacing={3}>
            <FormikTextView name="username" label="username" sx={{ mt: 1 }} formik={formik} />
            <FormikPasswordView
              name="password"
              label="password"
              type={showPassword ? 'text' : 'password'}
              sx={{ mt: 1 }}
              formik={formik}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => setShowPassword(!showPassword)}>
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <LoadingButton variant="contained" size="large" type="submit" loading={isLoading}>
              <T>login</T>
            </LoadingButton>
          </Stack>
        </Form>
      )}
    </Formik>
  );
}
